import type { FC } from "react"
import { useCallback } from "react"

import { AS400_VOID_SKIPPED_WARNING } from "../constants/invoiceAs400Sync"
import { useVoidInvoice } from "../hooks/useVoidInvoice"

import { InvoiceCommentConfirmDialog } from "./InvoiceCommentConfirmDialog"

import { EntityLockBanner, EntityLockConflictDialog, useEntityLock, useEntityLockConflictDialog } from "@/entityLock"
import { MutationLockResultStatus } from "@/entityLock/types"
import type { Invoice } from "@/graphql/codegen/graphql"
import { EntityLockEntityType, As400TransactionSyncStatus as SyncStatus } from "@/graphql/codegen/graphql"

interface InvoiceVoidDialogProps {
  invoice: Invoice
  onClose: () => void
  onSuccess: () => void
  onRefresh?: () => void
}

export const InvoiceVoidDialog: FC<InvoiceVoidDialogProps> = ({ invoice, onClose, onSuccess, onRefresh }) => {
  const { voidInvoice, isVoiding } = useVoidInvoice()
  // AK-5648 AC4: a payment that never reached AS400 means the void is AEROS-only, even if it was reconciled by hand.
  // The backend skips the AS400 reconciliation whenever the payment sync is not Synced; a running one blocks the void.
  const isPaymentOutOfSync = [SyncStatus.NotSynced, SyncStatus.Failed, SyncStatus.ManuallyResolved].includes(
    invoice.as400PaidSyncStatus
  )

  const { holder, isReadOnly, markLost } = useEntityLock({
    entityType: EntityLockEntityType.Invoice,
    entityId: invoice.invoiceId,
    enabled: true,
  })

  const { isLockConflictOpen, lockConflictMessage, openLockConflict, closeLockConflict, handleLockConflictRefresh } =
    useEntityLockConflictDialog({ onRefresh, markLost })

  const handleConfirm = useCallback(
    async (comment: string) => {
      const result = await voidInvoice({ invoiceId: invoice.invoiceId, comment })
      if (result.status === MutationLockResultStatus.Success) {
        onSuccess()
        return
      }
      if (result.status === MutationLockResultStatus.LockConflict) {
        openLockConflict(result.message)
      }
    },
    [voidInvoice, invoice.invoiceId, openLockConflict, onSuccess]
  )

  if (isLockConflictOpen && lockConflictMessage) {
    return (
      <EntityLockConflictDialog
        message={lockConflictMessage}
        onClose={closeLockConflict}
        onRefresh={handleLockConflictRefresh}
      />
    )
  }

  return (
    <InvoiceCommentConfirmDialog
      title='Are you sure you want to void this invoice?'
      content={isPaymentOutOfSync ? AS400_VOID_SKIPPED_WARNING : "This action cannot be undone."}
      commentLabel='Void Comment'
      commentPlaceholder='Enter void comment...'
      confirmLabel='Yes, Void Invoice'
      closeLabel='Go Back'
      warning
      isProcessing={isVoiding}
      isSubmitDisabled={isReadOnly}
      banner={isReadOnly ? <EntityLockBanner holder={holder} /> : undefined}
      onClose={onClose}
      onConfirm={handleConfirm}
      testIdPrefix='invoices.void-dialog'
    />
  )
}
